import http from './index'
import { unload } from '@/track/trackInit'
import type { MediaImageItem } from '@/types/media'
import type { RelatedWorkCandidatesResponse } from '@/types/relatedWork'

//*** 全局通用接口 ***//

interface Image2ImageParams {
  prompt: string
  strength?: string
  sourceImage: File
  modelId?: string
  batchSize?: number
  topicId?: string // 需注释
  parentGenerateId?: string //如果是流转过来的图片，需要携带原始ID
  tagId?: string
  modelStyle?: string
}

interface Kontext2ImageParams {
  prompt: string
  modelId?: string
  parentGenerateId?: string
  topicId?: string
  modelStyle?: string
  algoTopicId?: string // 场景标识：series_blindbox 造盲盒 / multi_view 多视图
  sourceImageUrls?: string[] // 参考图 URL 数组
  situation?: string // 输入图片场景标识：blindbox 造盲盒 / multiview 多视图，回填在结果图 imageExtraInfo.inputExtraInfo.situation
}

interface Image2VideoParams {
  prompt: string
  sourceImage: File
  modelId?: string
  parentGenerateId?: string
  topicId?: string
  tagId?: string
  duration?: number
}

interface InpaintingBrushGenerateParams {
  sourceImage: File
  maskImage: File
  prompt?: string
}

interface GetUserPostListParams {
  page: number
  pageSize: number
  designerUserId: string
  after?: string
  sessionId?: string
}

export const pageSize = 20

// 获取历史记录
const getHistoryList = (
  page: number,
  target: 'image' | 'model3d' | 'video',
  size?: number,
  controller?: AbortController,
  from?: 'edu' | 'mainsite' | 'challenge', // 来源 教育-首页-挑战
  topicId?: string,
  reviewStatus?: boolean,
  isPublish?: boolean //屏蔽盲盒封面和隐藏款剪影，发布场景（盲盒/普通发布选择列表）需传 true，默认 false
) => {
  return http.get(
    '/aigc/api/generate/user/list',
    {
      page,
      pageSize: size ?? pageSize,
      designerUserId: '',
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      target,
      source: from === 'edu' ? 1 : from === 'challenge' ? 0 : undefined,
      topicId: topicId || '',
      reviewStatus,
      isPublish: isPublish ?? false
    },
    {
      controller
    }
  )
}

// 获取正在运行的jobs
const getRunningJobIds = (topicId: string, target?: string) => {
  return http.post(
    '/aigc/api/generate/get_pending_jobs',
    {
      topicId,
      target
    },
    {
      errorModalAutoClose: false
    }
  )
}

// 文生图
const text2Image = (prompt: string, modelId?: string, topicId?: string, modelStyle?: string) => {
  return http.post(
    '/aigc/api/generate',
    {
      prompt,
      modelId,
      topicId,
      modelStyle
    },
    {
      errorModalAutoClose: false
    }
  )
}

// 图生图
const image2Image = ({
  prompt,
  strength,
  sourceImage,
  modelId,
  batchSize,
  topicId, // 键帽活动-后面需注释
  parentGenerateId,
  tagId,
  modelStyle
}: Image2ImageParams) => {
  return http.postForm(
    '/aigc/api/generate/img2img',
    {
      tagId,
      prompt,
      modelId,
      sourceImage,
      batchSize,
      modelStyle,
      topicId, // 键帽活动-后面需注释
      parentGenerateId: parentGenerateId
    },
    {
      errorModalAutoClose: false
    }
  )
}

// 三个业务场景（通用文生图/盲盒封面/隐藏款蒙层）共用同一个 kontext 生成接口，统一出口避免 URL/请求配置各处重复
const postKontextGenerate = (body: Record<string, unknown>) => {
  return http.postForm('/aigc/api/generate/kontext', body, {
    errorModalAutoClose: false
  })
}

// Kontext 文生图
const kontext2Image = ({
  prompt,
  modelId,
  parentGenerateId,
  modelStyle,
  algoTopicId,
  sourceImageUrls,
  situation
}: Kontext2ImageParams) => {
  return postKontextGenerate({
    prompt,
    modelId,
    parentGenerateId: parentGenerateId,
    modelStyle: modelStyle,
    algoTopicId,
    sourceImageUrls,
    ...(situation ? { situation } : {})
  })
}

// 造盲盒/多视图 骰子推荐文案
const getRecommendPrompt = () => {
  return http.get(
    '/aigc/api/agent/chat/recommend-prompt',
    {},
    {
      isShowErrorModal: false
    }
  )
}

interface BlindBoxCoverGenerateParams {
  sourceImageUrls: string[] // 6~7 张款式图片 URL（按坑位顺序，有隐藏款时也一并传入）
  parentGenerateId: string // 取 slots+隐藏款中第一张非本地上传图的 uniqueImageId
}

// 盲盒封面图生成
const blindBoxCoverGenerate = ({
  sourceImageUrls,
  parentGenerateId
}: BlindBoxCoverGenerateParams) => {
  return postKontextGenerate({
    sourceImageUrls,
    algoTopicId: 'series_blindbox_cover',
    situation: 'blindbox',
    parentGenerateId
  })
}

interface BlindBoxSilhouetteGenerateParams {
  sourceImageUrls: string[] // 1 张隐藏款原图 URL；统一为 A.4a 字段名，与 blindBoxCoverGenerate 一致
  parentGenerateId: string
}

// 隐藏款蒙层图生成（仅 hiddenSlot 非空时调用）
const blindBoxSilhouetteGenerate = ({
  sourceImageUrls,
  parentGenerateId
}: BlindBoxSilhouetteGenerateParams) => {
  return postKontextGenerate({
    sourceImageUrls,
    algoTopicId: 'secret_edition_card',
    situation: 'blindbox',
    parentGenerateId
  })
}

interface BlindBoxPreCheckParams {
  topicId?: string // 挑战话题 id；未选话题时可为空
  items: { generateId: string; itemId: string }[] // 盲盒帖图片列表（顺序不限）
}

// 图片发布状态预校验（盲盒专属批量接口，与单图路径 postPreCheck 不可复用）
const blindBoxPreCheck = (params: BlindBoxPreCheckParams) => {
  return http.post('/aigc/api/topic/content/blind-box/pre-check', params)
}

// Topic 选择列表（A.6，复用 AppPostChallenge.getChallenges 同一接口/参数，一次性拉全量）
const getTopicSelection = () => {
  return http.get('/aigc/api/topic/selection', {
    page: 1,
    pageSize: 1000,
    showWantable: 1
  })
}

interface BlindBoxPublishParams {
  contentType: 'BLINDBOX' // 注意字段名是 contentType 不是 postType，取值大写 BLINDBOX
  topicId?: string
  title: string
  description: string
  posterImage: MediaImageItem[] // 封面图（store.generation.cover.image）
  contentImage: Array<MediaImageItem & { isHidden?: boolean }> // 普通款按坑位顺序 + 隐藏款（有则排末尾，isHidden:true）
  hiddenPrompt: number
  showCustomizeBuyButton: number
  mentionUser: Array<{ userId: string; startIndex: number; endIndex: number }>
}

// 盲盒帖发布（A.7，与路径一共用 endpoint，contentType 区分场景）；isShowErrorModal:false，
// 由调用方按 40410/40411/40412 等错误码映射专属文案后自行 toast，避免和 httpClient 默认弹窗重复
const blindBoxPublish = (params: BlindBoxPublishParams) => {
  return http.post('/aigc/api/topic/content/general', params, {
    isShowErrorModal: false
  })
}

// 图生视频
const image2Video = (params: Image2VideoParams) => {
  return http.postForm('/aigc/api/generate/img2video', params, {
    errorModalAutoClose: false
  })
}

// 图转3D
const imageTo3D = (sourceImage: File, parentGenerateId?: string, voxelResolution?: string) => {
  return http.postForm(
    '/aigc/api/generate/img2model3d',
    { sourceImage, parentGenerateId: parentGenerateId, voxelResolution: voxelResolution },
    {
      errorModalAutoClose: false
    }
  )
}

// 图片重绘
const brushDesign = (params: InpaintingBrushGenerateParams) => {
  return http.postForm('/aigc/api/generate/inpaint', params, {
    errorModalAutoClose: false
  })
}

// 图片高清
const imageToHD = (sourceImage: File, parentGenerateId?: string, topicId?: string) => {
  return http.postForm(
    '/aigc/api/generate/upscale',
    {
      sourceImage,
      batchSize: 1,
      parentGenerateId: parentGenerateId,
      topicId
    },
    {
      errorModalAutoClose: false
    }
  )
}

// status 为数字枚举（0 排队/1 生成中/2 成功/3 失败），与 WS 进度消息用的是同一套枚举，
// 判定逻辑统一走 BlindBoxPublish/index.vue 引入的 toStatus
export interface GenerateResultData {
  jobId: string
  status: number
  percentage: number
  images?: MediaImageItem[]
}

// 结果查询
const queryResult = (jobId: string) => {
  return http.get<GenerateResultData>(
    '/aigc/api/generate/result',
    { jobId },
    {
      isShowErrorModal: false
    }
  )
}

// 获取生成图片的原图
const getOriginalImage = async (uniqueImageId: string) => {
  const res = await fetch(`/aigc/api/generate/painter/image?uniqueImageId=${uniqueImageId}`)
  if (!res.ok) throw new Error('Failed to fetch original image')
  return res.blob()
}

// 获取学生优惠信息
const getStuDiscount = (modelType: string) => {
  return http.get(`/aigc/trade/product/discount?modelType=${modelType}`)
}

// 短链生成
const shareInfo = (params: any) => {
  return http.post('/aigc/api/share/info', params, {
    errorModalAutoClose: false,
    isShowErrorModal: false
  })
}

// 删除post信息
const deletePostDetail = (params: any) => {
  return http.post('/aigc/api/topic/content/batch/delete', params, {
    errorModalAutoClose: false,
    isShowErrorModal: false
  })
}
// 分享时判断作品状态
const getDetailShare = (params: any) => {
  return http.post('/aigc/api/topic/content/share', params, {
    errorModalAutoClose: false,
    isShowErrorModal: false
  })
}
// 下载时判断作品状态
const getDetailDownload = (params: any) => {
  return http.post('/aigc/api/topic/content/download', params, {
    errorModalAutoClose: false,
    isShowErrorModal: false
  })
}
// wantIt时判断作品状态
const getDetailwantIt = (params: any) => {
  return http.post('/aigc/api/topic/content/want', params, {
    errorModalAutoClose: false,
    isShowErrorModal: false
  })
}

// 获取模型列表
// 1: 生活好物 2: 以图生图
const getModelList = (scene: number) => {
  return http.get('/aigc/api/model/selection', {
    page: 1,
    pageSize: 1000,
    scene
  })
}

// 获取上新New标识的配置
const getNewTagConfig = () => {
  return http.get('/aigc/api/new/config')
}

// 获取系列详情
const querySeriesDetail = (uniqueSeriesId: string) => {
  return http.get(`/aigc/api/topic/content/series/detail?uniqueSeriesId=${uniqueSeriesId}`)
}

// 获取系列内容列表
const querySeriesContentList = (data: {
  page: number
  pageSize: number
  uniqueSeriesId: string
}) => {
  return http.get(
    `/aigc/api/topic/content/series/content/list?page=${data.page}&pageSize=${data.pageSize}&uniqueSeriesId=${data.uniqueSeriesId}`
  )
}

const createSeries = (data: any) => {
  return http.put(`/aigc/api/topic/content/series`, data)
}

// 获取用户系列列表
const getUserSeriesList = (data: { page: number; pageSize: number; designerUserId: string }) => {
  return http.get(`/aigc/api/topic/content/series/user/list`, data)
}

// 获取个人作品列表
const getUserPostList = (params: GetUserPostListParams) => {
  return http.get('/aigc/api/topic/content/user/list', {
    ...params,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  })
}

const deleteItems = (itemIds: string[], target: 'image' | 'model3d' | 'video') => {
  return http.post('/aigc/api/generate/delete/item', { itemIds, target })
}

const downloadSingleVideo = (id: string, uniqueContentId?: string) => {
  return http.get('/aigc/api/download/video/single', {
    uniqueVideoId: id,
    uniqueContentId: uniqueContentId || ''
  })
}

// 批量下载视频
const downloadVideos = (ids: string[]) => {
  return http.get('/aigc/api/download/video/batch', { videoIdList: ids.join(',') })
}

const downloadVideosV2 = (downloadItems: { videoId: string; contentId: string }[]) => {
  return http.post('/aigc/api/download/video/batch/v2', downloadItems)
}
// 查询下载结果
const getDownloadRes = (taskId: string) => {
  return http.get('/aigc/api/download/video/batch/result', { taskId })
}

// 下载视频
const downLoadVideo = (taskId: string) => {
  let url = '/aigc/api/download/video/batch/download'
  url = `${url}?${new URLSearchParams({ taskId }).toString()}`
  const a = document.createElement('a')
  a.href = url
  a.download = 'videos.zip'
  a.click()
}

// 发布到挑战前的校验
const postPreCheck = (params: { itemId: string; generateId: string; topicId?: string }) => {
  return http.post('/aigc/api/topic/content/pre-check', params)
}

// 视频贴的下载，下载图片和视频的zip
const downloadVideoPost = (contentId: string) => {
  return http.get('/aigc/api/download/video', { contentId, isEnterFromEdu: false })
}

// 作品详情页
const getContentDetail = (contentId: string) => {
  return http.get('/aigc/api/topic/content/detail', { contentId })
}

// 挑战（话题）详情
const getTopicDetail = (topicId: string) => {
  return http.get('/aigc/api/topic/detail', { topicId })
}

// 挑战（话题）热门作品
const getTopicHotPosts = (params: { topicId: string; page?: number; pageSize?: number }) => {
  return http.get('/aigc/api/posts/topic/hot', params)
}

// 一级评论
const getCommentList = (contentId: string, limit: number = 20) => {
  return http.get('/aigc/api/comments/list', { contentId, limit })
}

const downloadFile = (blob: Blob, fileName: string) => {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = fileName
  a.click()
  URL.revokeObjectURL(a.href)
}

const downloadByTag = (url: string) => {
  // a标签的下载会出发浏览器的beforeunload事件，所以需要先移除，再添加
  window.removeEventListener('beforeunload', unload)

  const a = document.createElement('a')
  a.href = url
  a.click()

  window.addEventListener('beforeunload', unload)
}

// 获取销售记录
const getSalesRecord = (params: any) => {
  return http.get('/aigc/trade/order/user/sale', params)
}
// 获取销售记录
const getActivityShortTips = (params?: any, options?: any) => {
  return http.get('/aigc/api/activity/short-tips', params, options)
}

const getRelatedWorkCandidates = (params: { page: number; pageSize: number }) => {
  return http.get<RelatedWorkCandidatesResponse>(
    '/aigc/api/topic/content/relation/candidates',
    params
  )
}

export default {
  getDetailShare,
  getDetailDownload,
  deletePostDetail,
  text2Image,
  queryResult,
  image2Image,
  getHistoryList,
  getOriginalImage,
  imageTo3D,
  brushDesign,
  getStuDiscount,
  imageToHD,
  getDetailwantIt,
  getModelList,
  getNewTagConfig,
  getRunningJobIds,
  querySeriesDetail,
  querySeriesContentList,
  createSeries,
  shareInfo,
  getUserSeriesList,
  getUserPostList,
  kontext2Image,
  getRecommendPrompt,
  blindBoxCoverGenerate,
  blindBoxSilhouetteGenerate,
  blindBoxPreCheck,
  getTopicSelection,
  blindBoxPublish,
  image2Video,
  deleteItems,
  downloadVideos,
  getDownloadRes,
  downloadFile,
  downLoadVideo,
  downloadByTag,
  downloadSingleVideo,
  postPreCheck,
  downloadVideoPost,
  downloadVideosV2,
  getContentDetail,
  getTopicDetail,
  getTopicHotPosts,
  getCommentList,
  getSalesRecord,
  getActivityShortTips,
  getRelatedWorkCandidates
}
